import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum, IFile } from '@repo/toolkit/types'
import { CameraIcon, PenIcon, UserFourIcon, XIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import React, { useRef } from 'react'
import { toast } from 'sonner'

import { useModalManager } from '../../../lib/hooks/use-modal-manager'

type ProfileImageInputProps = {
  title: string
  file?: IFile | null
  toggleClassName?: string
  iconColor?: TailwindColor
  isLoading?: boolean
  maxImageSizeMB?: number
  disabled?: boolean
  onFileChange: (file: IFile) => void
}

export const ProfileImageInput: React.FC<ProfileImageInputProps> = ({
  title,
  file,
  toggleClassName,
  iconColor,
  isLoading = false,
  maxImageSizeMB = 2,
  disabled,
  onFileChange
}) => {
  const ref = useRef<HTMLInputElement>(null)
  const { modalState, toggleModal } = useModalManager(['file'])

  return (
    <div>
      <input
        type="file"
        hidden
        ref={ref}
        accept="image/jpeg,image/png"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return

          const maxSizeMB = maxImageSizeMB || 2
          const allowedExtensions = ['image/jpeg', 'image/png']

          // Check file size (max 2MB)
          if (file.size > maxSizeMB * 1024 * 1024) {
            toast.error('Le fichier ne doit pas dépasser 2 Mo.')
            return
          }

          // Check file type
          if (!allowedExtensions.includes(file.type)) {
            toast.error('Seuls les fichiers JPG, JPEG ou PNG sont autorisés.')
            return
          }

          onFileChange({
            name: file.name,
            size: file.size,
            path: URL.createObjectURL(file),
            type: FileTypeEnum.IMAGE
          })
        }}
      />
      <div className="relative w-fit">
        <div className="rounded-2xl w-20 h-20 object-cover bg-grey-light-hover flex justify-center items-center">
          {file ? (
            <img
              src={file?.path}
              alt="profile"
              className="w-full h-full object-cover rounded-2xl cursor-pointer"
              onClick={() => {
                toggleModal('file')
              }}
            />
          ) : (
            <UserFourIcon filled className="w-full h-full" color={tailwindColors.white.DEFAULT} />
          )}
        </div>
        {!disabled && (
          <div
            className="bg-white w-8 h-8 rounded-full absolute bottom-1 -right-2 flex justify-center items-center cursor-pointer group p-[2px]"
            onClick={() => {
              ref.current?.click()
            }}
          >
            <div
              className={cn(
                'w-full h-full rounded-full flex justify-center items-center p-[2px]',
                {
                  'bg-primary': !file,
                  'bg-secondary-light': !!file
                },
                toggleClassName
              )}
            >
              {isLoading ? (
                <Loader center size="xs" />
              ) : file ? (
                <PenIcon
                  className="w-4 h-4 "
                  color={iconColor ?? tailwindColors.secondary.DEFAULT}
                  fill={tailwindColors.white.DEFAULT}
                />
              ) : (
                <CameraIcon
                  color={iconColor ?? tailwindColors.white.DEFAULT}
                  className="w-full h-full"
                />
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalState.file}
        onCancel={() => toggleModal('file')}
        title={title}
        hideHeader
        hideActions
      >
        {file && (
          <div className="relative">
            <img
              src={file.path || 'https://via.placeholder.com/150'}
              alt="profile"
              className=" w-full rounded-xl h-96 object-contain"
            />
            <button
              aria-label="Close"
              className={cn(
                'p-1 hover:bg-grey-light-hover/50 rounded-full transition-colors duration-200 ease-in-out absolute top-0 right-0 m-4'
              )}
              onClick={() => {
                toggleModal('file')
              }}
            >
              <XIcon color={tailwindColors.black.DEFAULT} height="15px" width="15px" />
            </button>
          </div>
        )}
      </Modal>
    </div>
  )
}
